import React, { useMemo } from 'react';
import { Container, Row, Col } from 'reactstrap';
import { Link } from 'react-router-dom';
import { CellContext } from '@tanstack/react-table';
import { DataTable } from 'components/DataTable';
import { DataTableColumn, TableQueryParams, PaginatedResponse } from 'types/table.types';
import { SHOP_PATHS } from 'routes/routesConstants';
import { useShop } from 'context/shopContext';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { DataTableApiService } from 'services/api/tableApi';
import { formatRelativeDate } from 'utils/dateFormatter';
import { fmt } from 'utils/common';

// ─── Type ─────────────────────────────────────────────────────────────────────

interface SellAdjustmentItem {
  id: string;
  sellAdjustmentId: string;
  shopItemId: string;
  shopItemName?: string;
  invoiceId: string;
  adjustmentReason: string;
  adjustmentStatus: string;
  originalQuantity: number;
  originalUnitPrice: number;
  quantityDelta: number;
  priceDelta: number;
  discountDelta: number;
  adjustmentTypes: string[];
  notes?: string;
  createdAt: string;
  updatedAt?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Sell-side financial effect (money received = positive)
 * Price decrease (6→3) = we receive less money → negative
 */
function calcNetDelta(item: SellAdjustmentItem): number {
  const newPrice = item.originalUnitPrice + item.priceDelta;
  const priceEffect = item.originalQuantity * item.priceDelta;
  const qtyEffect = item.quantityDelta * newPrice;
  return priceEffect + qtyEffect - item.discountDelta;
}

const TYPE_META: Record<string, { label: string; color: string; bg: string }> = {
  QuantityChange: { label: 'Qty change', color: '#2563eb', bg: '#eff6ff' },
  PriceChange: { label: 'Price change', color: '#059669', bg: '#ecfdf5' },
  ItemRemoval: { label: 'Item removal', color: '#dc2626', bg: '#fef2f2' },
  DiscountChange: { label: 'Discount change', color: '#d97706', bg: '#fffbeb' },
};

// ─── Mini components ──────────────────────────────────────────────────────────

const TypePill: React.FC<{ type: string }> = ({ type }) => {
  const m = TYPE_META[type] ?? { label: type, color: '#6b7280', bg: '#f3f4f6' };
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 600,
        background: m.bg,
        color: m.color,
        whiteSpace: 'nowrap',
        lineHeight: 1.6,
      }}
    >
      {m.label}
    </span>
  );
};

const DeltaChip: React.FC<{ value: number; suffix?: string }> = ({ value, suffix = '' }) => {
  if (value === 0) return <span style={{ color: '#9ca3af', fontSize: 13 }}>—</span>;
  const positive = value > 0;
  return (
    <span
      style={{
        fontFamily: "'JetBrains Mono','Fira Code',monospace",
        fontSize: 12.5,
        fontWeight: 700,
        color: positive ? '#059669' : '#dc2626',
      }}
    >
      {positive ? '+' : ''}
      {value}
      {suffix}
    </span>
  );
};

const MoneyChip: React.FC<{ value: number }> = ({ value }) => {
  if (value === 0) return <span style={{ color: '#9ca3af', fontSize: 13 }}>—</span>;
  const positive = value > 0;
  return (
    <span
      style={{
        fontFamily: "'JetBrains Mono','Fira Code',monospace",
        fontSize: 12.5,
        fontWeight: 700,
        color: positive ? '#059669' : '#dc2626',
      }}
    >
      {positive ? '+' : ''}
      {fmt(value)}
    </span>
  );
};

const OriginalSnapshot: React.FC<{
  qty: number;
  price: number;
  qtyDelta: number;
  priceDelta: number;
}> = ({ qty, price, qtyDelta, priceDelta }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    {/* Quantity line */}
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
      <span style={{ color: '#9ca3af', minWidth: 28 }}>Qty</span>
      <span
        style={{
          fontFamily: 'monospace',
          color: '#374151',
          textDecoration: qtyDelta !== 0 ? 'line-through' : 'none',
          opacity: qtyDelta !== 0 ? 0.55 : 1,
        }}
      >
        {qty}
      </span>
      {qtyDelta !== 0 && (
        <>
          <span style={{ color: '#d1d5db', fontSize: 11 }}>→</span>
          <span
            style={{
              fontFamily: 'monospace',
              fontWeight: 700,
              color: qtyDelta > 0 ? '#059669' : '#dc2626',
            }}
          >
            {qty + qtyDelta}
          </span>
          <span style={{ fontSize: 10, color: '#9ca3af' }}>
            ({qtyDelta > 0 ? '+' : ''}
            {qtyDelta})
          </span>
        </>
      )}
    </div>
    {/* Price line */}
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
      <span style={{ color: '#9ca3af', minWidth: 28 }}>Price</span>
      <span
        style={{
          fontFamily: 'monospace',
          color: '#374151',
          textDecoration: priceDelta !== 0 ? 'line-through' : 'none',
          opacity: priceDelta !== 0 ? 0.55 : 1,
        }}
      >
        {fmt(price)}
      </span>
      {priceDelta !== 0 && (
        <>
          <span style={{ color: '#d1d5db', fontSize: 11 }}>→</span>
          <span
            style={{
              fontFamily: 'monospace',
              fontWeight: 700,
              color: priceDelta > 0 ? '#dc2626' : '#059669',
            }}
          >
            {fmt(price + priceDelta)}
          </span>
        </>
      )}
    </div>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const SellAdjustmentItemsList: React.FC = () => {
  const { shop } = useShop();
  const shopId = shop?.id ?? '';

  const columns = useMemo<DataTableColumn<SellAdjustmentItem>[]>(
    () => [
      // ── Item name → links to invoice ─────────────────────────────────────────
      {
        accessorKey: 'shopItemName',
        id: 'shopItemName',
        header: 'Item',
        enableSorting: false,
        enableFiltering: true,
        cell: (info: CellContext<SellAdjustmentItem, unknown>) => {
          const row = info.row.original;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Link
                to={SHOP_PATHS.MEDICINE_SELL_INVOICE_DETAIL.replace(':shopId', shopId).replace(
                  ':invoiceId',
                  row.invoiceId,
                )}
                style={{ fontWeight: 600, fontSize: 13, color: '#0d0f12', textDecoration: 'none' }}
              >
                {(info.getValue() as string) || '—'}
              </Link>
              {/* Adjustment types as pills under the name */}
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {Array.isArray(row.adjustmentTypes) ? (
                  row.adjustmentTypes.map((t) => <TypePill key={t} type={t} />)
                ) : (
                  <TypePill type={row.adjustmentTypes} />
                )}
              </div>
            </div>
          );
        },
      },

      // ── What changed (original → new, visual) ─────────────────────────────────
      {
        accessorKey: 'originalQuantity',
        id: 'changes',
        header: 'What changed',
        enableSorting: false,
        enableFiltering: false,
        cell: (info: CellContext<SellAdjustmentItem, unknown>) => {
          const row = info.row.original;
          return (
            <OriginalSnapshot
              qty={row.originalQuantity}
              price={row.originalUnitPrice}
              qtyDelta={row.quantityDelta}
              priceDelta={row.priceDelta}
            />
          );
        },
      },

      // ── Net effect on invoice total ────────────────────────────────────────────
      {
        accessorKey: 'quantityDelta',
        id: 'netEffect',
        header: 'Invoice effect',
        enableSorting: false,
        enableFiltering: false,
        cell: (info: CellContext<SellAdjustmentItem, unknown>) => {
          const row = info.row.original;
          const net = calcNetDelta(row);
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <MoneyChip value={net} />
              {row.discountDelta !== 0 && (
                <div style={{ fontSize: 11, color: '#9ca3af' }}>
                  Disc: <DeltaChip value={row.discountDelta} suffix="%" />
                </div>
              )}
            </div>
          );
        },
      },

      // ── Reason ────────────────────────────────────────────────────────────────
      {
        accessorKey: 'adjustmentReason',
        id: 'adjustmentReason',
        header: 'Reason',
        enableSorting: false,
        enableFiltering: true,
        cell: (info: CellContext<SellAdjustmentItem, unknown>) => {
          const reason = info.getValue() as string;
          const notes = (info.row.original as SellAdjustmentItem).notes;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 220 }}>
              <span style={{ fontSize: 13, color: '#374151' }}>{reason || '—'}</span>
              {notes && (
                <span
                  style={{
                    fontSize: 11,
                    color: '#9ca3af',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {notes}
                </span>
              )}
            </div>
          );
        },
      },

      // ── Invoice link ──────────────────────────────────────────────────────────
      {
        accessorKey: 'invoiceId',
        id: 'invoiceId',
        header: 'Invoice',
        enableSorting: false,
        enableFiltering: false,
        cell: (info: CellContext<SellAdjustmentItem, unknown>) => {
          const row = info.row.original;
          return (
            <Link
              to={SHOP_PATHS.MEDICINE_SELL_INVOICE_DETAIL.replace(':shopId', shopId).replace(
                ':invoiceId',
                row.invoiceId,
              )}
              style={{
                fontFamily: "'JetBrains Mono','Fira Code',monospace",
                fontSize: 11.5,
                color: '#2563eb',
                textDecoration: 'none',
              }}
            >
              View invoice →
            </Link>
          );
        },
      },

      // ── Status ────────────────────────────────────────────────────────────────
      {
        accessorKey: 'adjustmentStatus',
        id: 'adjustmentStatus',
        header: 'Status',
        enableSorting: true,
        enableFiltering: true,
        cell: (info: CellContext<SellAdjustmentItem, unknown>) => {
          const status = info.getValue() as string;
          const isPosted = status === 'Posted';
          return (
            <span
              style={{
                padding: '2px 9px',
                borderRadius: 20,
                fontSize: 11,
                fontWeight: 600,
                background: isPosted ? '#ecfdf5' : '#fffbeb',
                color: isPosted ? '#059669' : '#d97706',
              }}
            >
              {status || '—'}
            </span>
          );
        },
      },

      // ── Date ─────────────────────────────────────────────────────────────────
      {
        accessorKey: 'createdAt',
        id: 'createdAt',
        header: 'Date',
        enableSorting: true,
        enableFiltering: false,
        cell: (info: CellContext<SellAdjustmentItem, unknown>) => {
          const { text, isStale } = formatRelativeDate(info.getValue() as string);
          return (
            <span
              style={{
                fontSize: 12,
                color: isStale ? '#dc2626' : '#6b7280',
              }}
            >
              {text}
            </span>
          );
        },
      },
    ],
    [shopId],
  );

  const handleFetchData = async (
    params: TableQueryParams,
  ): Promise<PaginatedResponse<SellAdjustmentItem>> => {
    return DataTableApiService.fetchPaginatedData<SellAdjustmentItem>(
      API_ENDPOINTS.TENANT.SELLS.ADJUSTMENT_ITEMS_PAGINATED.replace(':shopId', shopId),
      { ...params, filters: { ...params.filters, shopId } },
    );
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col lg="12">
            <div
              style={{
                marginBottom: 16,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <h5 style={{ margin: 0, fontWeight: 600 }}>Sell adjustments</h5>
                <p style={{ margin: 0, fontSize: 12, color: '#6b7280', marginTop: 3 }}>
                  Original invoices are never modified — every row here is a permanent correction
                  record.
                </p>
              </div>
            </div>

            <DataTable
              columns={columns}
              queryKey={API_ENDPOINTS.TENANT.SELLS.ADJUSTMENT_ITEMS_PAGINATED.replace(
                ':shopId',
                shopId,
              )}
              onFetchData={handleFetchData}
              enableSearch
              enableFiltering
              enablePagination
              enableSorting
              enableColumnVisibility
              enableColumnReorder={false}
              enableRowSelection={false}
              enableBulkActions={false}
              enableExport
              pageSizeOptions={[10, 20, 50, 100]}
              defaultPageSize={10}
              columnVisibilityStorageKey="sell_adjustment_items_visibility"
              columnOrderStorageKey="sell_adjustment_items_order"
              title=""
              emptyMessage="No sell adjustment items found."
              striped
              hover
              bordered
              responsive
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default SellAdjustmentItemsList;
